import { getSupabaseUrl, getSupabaseConfig } from '../../config/global';

/** Supabase storage takes TUS chunks of exactly this size, except the last. */
export const TUS_CHUNK_BYTES = 6 * 1024 * 1024;

export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

/** Storage signs an upload token for two hours. An upload that runs longer asks for a new one before then. */
export const TOKEN_RENEW_MS = 90 * 60 * 1000;

export type TusTarget = { bucket: string; path: string; token: string; contentType: string };

type SendOptions = {
  onProgress: (sentBytes: number) => void;
  signal: AbortSignal;
  retryDelaysMs?: number[];
};

function baseHeaders(token: string): Record<string, string> {
  return { 'Tus-Resumable': '1.0.0', 'x-signature': token, apikey: getSupabaseConfig().anonKey };
}

function metadata(values: Record<string, string>): string {
  const b64 = (text: string) => btoa(String.fromCharCode(...new TextEncoder().encode(text)));
  return Object.entries(values)
    .map(([key, value]) => `${key} ${b64(value)}`)
    .join(',');
}

export function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Upload cancelled', 'AbortError'));
      },
      { once: true }
    );
  });
}

const endpoint = () => `${getSupabaseUrl()}/storage/v1/upload/resumable/sign`;

/** Returns the upload's own address. */
export async function tusCreate(target: TusTarget, size: number, signal: AbortSignal): Promise<string> {
  const response = await fetch(endpoint(), {
    method: 'POST',
    signal,
    headers: {
      ...baseHeaders(target.token),
      'Upload-Length': String(size),
      'Upload-Metadata': metadata({
        bucketName: target.bucket,
        objectName: target.path,
        contentType: target.contentType,
      }),
    },
  });
  const location = response.headers.get('location');
  if (!response.ok || !location) throw new Error(`Could not start the upload (HTTP ${response.status})`);
  return new URL(location, endpoint()).toString();
}

/** CLOUD-1048: hosted storage refuses a TUS upload of zero bytes, so an empty file is one signed PUT. */
export async function putEmpty(target: TusTarget, signal: AbortSignal): Promise<void> {
  const objectPath = target.path.split('/').map(encodeURIComponent).join('/');
  const url = `${getSupabaseUrl()}/storage/v1/object/upload/sign/${
    target.bucket
  }/${objectPath}?token=${encodeURIComponent(target.token)}`;
  const response = await fetch(url, {
    method: 'PUT',
    signal,
    headers: { apikey: getSupabaseConfig().anonKey, 'Content-Type': target.contentType, 'x-upsert': 'true' },
    body: new Blob([]),
  });
  if (!response.ok) throw new Error(`Could not start the upload (HTTP ${response.status})`);
}

/** Where an earlier attempt left off, or null when storage no longer has that upload. */
export async function tusOffset(location: string, token: string, signal: AbortSignal): Promise<number | null> {
  const response = await fetch(location, { method: 'HEAD', signal, headers: baseHeaders(token) });
  if (response.status >= 400 && response.status < 500) return null;
  const offset = Number(response.headers.get('upload-offset'));
  if (!response.ok || !Number.isFinite(offset)) throw new Error(`HTTP ${response.status}`);
  return offset;
}

async function serverOffset(location: string, token: string, signal: AbortSignal): Promise<number> {
  const response = await fetch(location, { method: 'HEAD', signal, headers: baseHeaders(token) });
  const offset = Number(response.headers.get('upload-offset'));
  if (!response.ok || !Number.isFinite(offset)) throw new Error(`HTTP ${response.status}`);
  return offset;
}

/** Sends the file from where the server says it stopped. `token` gives a token that is still valid. */
export async function tusSend(
  location: string,
  file: Blob,
  token: () => Promise<string>,
  { onProgress, signal, retryDelaysMs = RETRY_DELAYS_MS }: SendOptions,
  resume = false
): Promise<void> {
  let offset = resume ? await serverOffset(location, await token(), signal) : 0;
  let failures = 0;
  onProgress(offset);

  while (offset < file.size) {
    const end = Math.min(offset + TUS_CHUNK_BYTES, file.size);
    try {
      const response = await fetch(location, {
        method: 'PATCH',
        signal,
        headers: {
          ...baseHeaders(await token()),
          'Upload-Offset': String(offset),
          'Content-Type': 'application/offset+octet-stream',
        },
        body: file.slice(offset, end),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      offset = Number(response.headers.get('upload-offset') ?? end);
      failures = 0;
      onProgress(offset);
    } catch (error) {
      if (signal.aborted) throw error;
      if (failures >= retryDelaysMs.length) throw new Error('The upload stopped. Check your connection.');
      await wait(retryDelaysMs[failures], signal);
      failures += 1;
      const known = offset;
      offset = await serverOffset(location, await token(), signal).catch(() => known);
    }
  }
}
